import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { RouteName } from './types'
import { i18n, setLocale, type Locale, SUPPORTED_LOCALES } from '../i18n'
import { useFeatureFlagsStore } from '@/store/featureFlags'
import { FeatureFlag } from '@/types/featureFlags'

const DYNAMIC_IMPORT_RELOAD_KEY = 'dynamic-import-reload-path'
const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
]

const isDynamicImportError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error)

  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => message.includes(pattern))
}

const reloadForDynamicImport = (targetPath: string): void => {
  const lastReloadPath = sessionStorage.getItem(DYNAMIC_IMPORT_RELOAD_KEY)

  if (lastReloadPath === targetPath) {
    sessionStorage.removeItem(DYNAMIC_IMPORT_RELOAD_KEY)
    return
  }

  sessionStorage.setItem(DYNAMIC_IMPORT_RELOAD_KEY, targetPath)
  window.location.assign(targetPath)
}

declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    description?: string
    canonicalPath?: string
    ogImage?: string
    ogImageAlt?: string
    ogType?: string
    twitterImageAlt?: string
    robots?: string
  }
}

// Eager-load main layout and cases page for instant display
import MasterLayout from '../layouts/MainLayout.vue'
import Cases from '../views/Cases/Cases.vue'

// lazy-loaded components
const CaseDetail = () => import('../views/CaseDetail/CaseDetail.vue')
const Giveaways = () => import('../views/Giveaways/Giveaways.vue')
const GiveawayDetail = () => import('../views/Giveaways/GiveawayDetail.vue')
const DailyFree = () => import('../views/DailyFree/DailyFree.vue')
const Events = () => import('../views/Events/Events.vue')
const TidesOfWar = () => import('../views/TidesOfWar/TidesOfWar.vue')
const Arcade = () => import('../views/Arcade/Arcade.vue')
const ArcadeWordle = () => import('../views/Arcade/Wordle.vue')
const ArcadePriceGuess = () => import('../views/Arcade/PriceGuess.vue')
const ArcadeMinesweeper = () => import('../views/Arcade/Minesweeper.vue')
const ClutchQuiz = () => import('../views/Arcade/ClutchQuiz.vue')
const TacticalRecall = () => import('../views/Arcade/TacticalRecall.vue')
const Arena = () => import('../views/Arena/Arena.vue')
const ArenaCreate = () => import('../views/Arena/ArenaCreate.vue')
const ArenaRoom = () => import('../views/Arena/ArenaRoom.vue')
const Upgrade = () => import('../views/Upgrade/Upgrade.vue')
const Contracts = () => import('../views/Contracts/Contracts.vue')
const SkinClash = () => import('../views/SkinClash/SkinClash.vue')
const Profile = () => import('../views/Profile/Profile.vue')
const PublicProfile = () => import('../views/Profile/PublicProfile.vue')
const Fairness = () => import('../views/Fairness/Fairness.vue')
const OddsHistory = () => import('../views/OddsHistory/OddsHistory.vue')
const PayopReturnView = () => import('../views/Deposit/PayopReturnView.vue')
const Support = () => import('../views/Support/Support.vue')
const Unsubscribe = () => import('../views/Unsubscribe/Unsubscribe.vue')
const WhaleProtocol = () => import('../views/WhaleProtocol/WhaleProtocol.vue')
const AlphaWhalesClub = () => import('../views/AlphaWhales/AlphaWhalesClub.vue')
// Legal pages
const PrivacyPolicy = () => import('../views/Legal/PrivacyPolicy.vue')
const WithdrawalPolicy = () => import('../views/Legal/WithdrawalPolicy.vue')
const AmlKyc = () => import('../views/Legal/AMLKYCProcedures.vue')
const TermsOfService = () => import('../views/Legal/TermsOfService.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/:locale(tr|es|ru|pl|de|ro)?',
    component: MasterLayout,
    children: [
      // Homepage - Main Cases Page
      {
        path: '',
        name: RouteName.CASES,
        component: Cases,
      },

      // Legacy /cases route - redirect to homepage
      {
        path: 'cases',
        redirect: { name: RouteName.CASES },
      },

      {
        path: 'giveaways',
        name: RouteName.GIVEAWAYS,
        component: Giveaways,
      },

      {
        path: 'giveaways/:id',
        name: RouteName.GIVEAWAY_DETAIL,
        component: GiveawayDetail,
        props: (route) => ({ id: String(route.params.id) }),
      },

      {
        path: 'daily-free',
        name: RouteName.DAILY_FREE,
        component: DailyFree,
      },

      {
        path: 'daily-free/:caseName',
        name: RouteName.DAILY_FREE_DETAIL,
        component: CaseDetail,
      },

      {
        path: 'events/:tab?',
        name: RouteName.EVENTS,
        component: Events,
      },
      {
        path: 'tides-of-war',
        name: RouteName.TIDES_OF_WAR,
        component: TidesOfWar,
      },
      {
        path: 'arcade',
        name: RouteName.ARCADE,
        component: Arcade,
      },
      {
        path: 'arcade/wordle',
        name: RouteName.ARCADE_WORDLE,
        component: ArcadeWordle,
      },
      {
        path: 'arcade/price-guess',
        name: RouteName.ARCADE_PRICE_GUESS,
        component: ArcadePriceGuess,
      },
      {
        path: 'arcade/minesweeper',
        name: RouteName.ARCADE_MINESWEEPER,
        component: ArcadeMinesweeper,
      },
      {
        path: 'arcade/clutch-quiz',
        name: RouteName.ARCADE_TRIVIA,
        component: ClutchQuiz,
      },
      {
        path: 'arcade/tactical-recall',
        name: RouteName.ARCADE_MEMORY,
        component: TacticalRecall,
      },
      {
        path: 'arena',
        name: RouteName.ARENA,
        component: Arena,
        meta: {
          title: 'Whale Duel | Skinwhales',
          description: 'Join multiplayer Whale Duel rooms and compete in round-based case battles.',
          ogType: 'website',
        },
      },
      {
        path: 'arena/create',
        name: RouteName.ARENA_CREATE,
        component: ArenaCreate,
        meta: {
          title: 'Create Whale Duel Room | Skinwhales',
          description: 'Create a new PvP or PvE Whale Duel room and invite others to join.',
          ogType: 'website',
        },
      },
      {
        path: 'arena/room/:shortId',
        name: RouteName.ARENA_ROOM,
        component: ArenaRoom,
        props: (route) => ({ shortId: String(route.params.shortId) }),
        meta: {
          title: 'Whale Duel Room | Skinwhales',
          description: 'Watch live Whale Duel rounds, ready up, and see room results in real time.',
          ogType: 'website',
        },
      },
      {
        path: 'upgrade',
        name: RouteName.UPGRADE,
        component: Upgrade,
        meta: {
          title: 'Upgrade Game | Skinwhales',
          description:
            'Upgrade your CS2 items to higher-value items. Select items, choose multiplier, and test your luck!',
          ogType: 'website',
        },
      },
      {
        path: 'contracts',
        name: RouteName.CONTRACTS,
        component: Contracts,
        meta: {
          title: 'Contracts Game | Skinwhales',
          description:
            'Craft CS2 item contracts: combine 3-10 skins, choose difficulty, and chase high-value payouts with provable fairness.',
          ogType: 'website',
        },
      },
      {
        path: 'skin-clash',
        name: RouteName.SKIN_CLASH,
        component: SkinClash,
        meta: {
          title: 'Skin Clash | CS2 Jackpot Game | Skinwhales',
          description:
            'Join Skin Clash rounds with READY inventory skins, collect tickets by item value, and chase the full shared pot with provably fair drawing.',
          ogType: 'website',
        },
      },
      {
        path: 'whale-protocol',
        name: RouteName.WHALE_PROTOCOL,
        component: WhaleProtocol,
        meta: {
          title: 'Whale Protocol | Safebox Game | Skinwhales',
          description: 'Defuse the bomb, guess the code, win the jackpot.',
          ogType: 'website'
        }
      },

      // /case/:caseName  (slug like "gamma-2-case")
      {
        path: 'case/:caseName',
        name: RouteName.CASE_DETAIL,
        component: CaseDetail,
        props: (route) => ({ caseName: String(route.params.caseName) }),
      },

      // /profile
      {
        name: RouteName.PUBLIC_PROFILE,
        path: 'profile/:userId([0-9a-fA-F-]{36})',
        component: PublicProfile,
        props: (route) => ({ userId: String(route.params.userId) }),
        meta: {
          title: 'Public Profile | Skinwhales',
          description: 'View user profile stats and level progression on Skinwhales.',
          ogType: 'profile',
        },
      },
      {
        name: RouteName.PROFILE,
        path: 'profile/:tab?',
        component: Profile,
        meta: {
          title: 'Account Dashboard | Skinwhales',
          description:
            'Manage your Skinwhales profile, track stats, and control trade settings in one place.',
          robots: 'noindex,follow',
          ogType: 'profile',
        },
      },

      // /provably-fair
      {
        name: RouteName.PROVABLY_FAIR,
        path: 'provably-fair',
        component: Fairness,
        meta: {
          title: 'Provably Fair CS2 Case Opening | Transparency Guaranteed | Skinwhales',
          description:
            'Skinwhales uses cryptographic provably fair technology with Random.org seeds, server hashes, and client seeds. Verify every CS2 case opening result yourself. 100% transparent and fair.',
          ogType: 'article',
        },
      },

      // /provably-fair/:caseName - Odds history for a specific case
      {
        name: RouteName.ODDS_HISTORY,
        path: 'provably-fair/:caseName',
        component: OddsHistory,
        props: (route) => ({ caseName: String(route.params.caseName) }),
        meta: {
          title: 'Case Odds History | Provably Fair | Skinwhales',
          description:
            'View complete historical odds and price changes for this CS2 case. Full transparency with provably fair verification.',
          ogType: 'article',
          robots: 'index,follow',
        },
      },
      {
        name: RouteName.PAYOP_RESULT,
        path: 'deposit/payop/result',
        component: PayopReturnView,
        props: () => ({ variant: 'result' as const }),
        meta: {
          title: 'PayOp Deposit Status | Skinwhales',
          description: 'Check the status of your PayOp deposit on Skinwhales.',
          robots: 'noindex,follow',
          ogType: 'website',
        },
      },
      {
        name: RouteName.PAYOP_FAILED,
        path: 'deposit/payop/failed',
        component: PayopReturnView,
        props: () => ({ variant: 'failed' as const }),
        meta: {
          title: 'PayOp Deposit Failed | Skinwhales',
          description: 'Review your PayOp deposit status and retry your payment on Skinwhales.',
          robots: 'noindex,follow',
          ogType: 'website',
        },
      },

      {
        name: RouteName.SUPPORT,
        path: 'support',
        component: Support,
        meta: {
          title: 'Support & FAQ | Skinwhales',
          description:
            'Find answers to common questions and learn how to get in touch with the Skinwhales support team.',
          robots: 'index,follow',
          ogType: 'website',
        },
      },
      {
        name: RouteName.ALPHA_WHALES,
        path: 'alpha-whales-club',
        component: AlphaWhalesClub,
        meta: {
          title: 'Alpha Whales Club | Skinwhales VIP',
          description:
            'Get concierge rewards, curated drops, and dedicated hosting inside the Skinwhales Alpha Whales Club.',
          robots: 'index,follow',
          ogType: 'website',
        },
      },

      {
        name: RouteName.UNSUBSCRIBE,
        path: 'unsubscribe',
        component: Unsubscribe,
        meta: {
          title: 'Unsubscribe from Mailing List | Skinwhales',
          description:
            'Manage your Skinwhales email preferences and unsubscribe from the marketing mailing list.',
          robots: 'noindex,follow',
          ogType: 'website',
        },
      },

      {
        path: 'legal/privacy-policy',
        name: RouteName.PRIVACY_POLICY,
        component: PrivacyPolicy,
      },
      {
        path: 'legal/withdrawal-policy',
        name: RouteName.WITHDRAWAL_POLICY,
        component: WithdrawalPolicy,
      },
      {
        path: 'legal/aml-kyc',
        name: RouteName.AML_KYC,
        component: AmlKyc,
      },
      {
        path: 'legal/terms-of-service',
        name: RouteName.TERMS_OF_SERVICE,
        component: TermsOfService,
      },
    ],
  },

  // Optional 404
  // { path: '/:pathMatch(.*)*', name: RouteName.NOT_FOUND, component: () => import('src/views/NotFound.vue') },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, saved) {
    if (saved) {
      return saved
    }
    if (to.name === RouteName.PROFILE && from.name === RouteName.PROFILE) {
      return false
    }
    if (to.name === RouteName.EVENTS && from.name === RouteName.EVENTS) {
      return false
    }
    return { top: 0 }
  },
})

router.onError((error, to) => {
  if (isDynamicImportError(error)) {
    console.warn('Dynamic route chunk failed to load, reloading once to refresh assets.', error)
    reloadForDynamicImport(to.fullPath)
    return
  }

  console.error('Router navigation failed:', error)
})

router.afterEach((_to, _from, failure) => {
  if (!failure) {
    sessionStorage.removeItem(DYNAMIC_IMPORT_RELOAD_KEY)
  }
})

// Navigation guard for locale detection and synchronization
router.beforeEach(async (to, from, next) => {
  const urlLocale = to.params.locale as string | undefined
  const savedLocale = localStorage.getItem('locale') as Locale | null
  const currentI18nLocale = i18n.global.locale.value

  // Determine the target locale
  let targetLocale: Locale = 'en'

  if (urlLocale && SUPPORTED_LOCALES.includes(urlLocale as Locale)) {
    // URL has a valid locale - use it
    targetLocale = urlLocale as Locale
  } else if (!urlLocale) {
    // URL has no locale (English or first visit)
    if (savedLocale && SUPPORTED_LOCALES.includes(savedLocale)) {
      // User has a saved preference - redirect to that locale if not English
      if (savedLocale !== 'en') {
        return next({
          name: to.name || RouteName.CASES,
          params: { ...to.params, locale: savedLocale },
          query: to.query,
          hash: to.hash,
        })
      }
      targetLocale = 'en'
    } else {
      // First-time visitor - detect from browser
      const browserLang = (navigator.language || 'en').toLowerCase()
      if (browserLang.startsWith('tr')) targetLocale = 'tr'
      else if (browserLang.startsWith('es')) targetLocale = 'es'
      else if (browserLang.startsWith('pl')) targetLocale = 'pl'
      else if (browserLang.startsWith('ru')) targetLocale = 'ru'
      else if (browserLang.startsWith('de')) targetLocale = 'de'
      else if (browserLang.startsWith('ro')) targetLocale = 'ro'
      else targetLocale = 'en'

      // Redirect to detected locale if not English
      if (targetLocale !== 'en') {
        return next({
          name: to.name || RouteName.CASES,
          params: { ...to.params, locale: targetLocale },
          query: to.query,
          hash: to.hash,
        })
      }
    }
  }

  // Sync i18n and localStorage with the target locale
  if (targetLocale !== currentI18nLocale) {
    await setLocale(targetLocale)
  }

  next()
})

// Navigation guard for feature flag checks
// Note: This guard runs after feature flags are initialized (router is installed after flags load in main.ts)
router.beforeEach((to, from, next) => {
  const featureFlagsStore = useFeatureFlagsStore()

  // Define routes that require feature flags
  const routeFeatureMap: Partial<Record<string, FeatureFlag>> = {
    [RouteName.CASES]: FeatureFlag.GAME_CASES,
    [RouteName.CASE_DETAIL]: FeatureFlag.GAME_CASES,
    [RouteName.DAILY_FREE]: FeatureFlag.GAME_CASES_DAILYFREE,
    [RouteName.DAILY_FREE_DETAIL]: FeatureFlag.GAME_CASES_DAILYFREE,
    [RouteName.ARCADE]: FeatureFlag.GAME_ARCADE_WORDFRAG,
    [RouteName.ARCADE_WORDLE]: FeatureFlag.GAME_ARCADE_WORDFRAG,
    [RouteName.ARCADE_MEMORY]: FeatureFlag.GAME_ARCADE_WORDFRAG,
    [RouteName.GIVEAWAYS]: FeatureFlag.GAME_GIVEAWAYS,
    [RouteName.GIVEAWAY_DETAIL]: FeatureFlag.GAME_GIVEAWAYS,
    [RouteName.EVENTS]: FeatureFlag.GAME_EVENTS,
    [RouteName.TIDES_OF_WAR]: FeatureFlag.GAME_EVENTS,
    [RouteName.UPGRADE]: FeatureFlag.GAME_UPGRADE,
    [RouteName.ARENA]: FeatureFlag.GAME_ARENA,
    [RouteName.ARENA_CREATE]: FeatureFlag.GAME_ARENA,
    [RouteName.ARENA_ROOM]: FeatureFlag.GAME_ARENA,
  }

  const routeName = to.name as string
  const requiredFlag = routeFeatureMap[routeName]

  // If route requires a feature flag and it's disabled, find first available game to redirect to
  if (requiredFlag && !featureFlagsStore.isEnabled(requiredFlag)) {
    console.warn(`Access denied: Feature flag ${requiredFlag} is disabled for route ${routeName}`)

    // Find first available game feature in priority order
    const fallbackRoutes = [
      { route: RouteName.GIVEAWAYS, flag: FeatureFlag.GAME_GIVEAWAYS },
      { route: RouteName.DAILY_FREE, flag: FeatureFlag.GAME_CASES_DAILYFREE },
      { route: RouteName.EVENTS, flag: FeatureFlag.GAME_EVENTS },
      { route: RouteName.CASES, flag: FeatureFlag.GAME_CASES },
      { route: RouteName.UPGRADE, flag: FeatureFlag.GAME_UPGRADE },
      { route: RouteName.ARENA, flag: FeatureFlag.GAME_ARENA },
      { route: RouteName.ARCADE, flag: FeatureFlag.GAME_ARCADE_WORDFRAG },
    ]

    for (const { route, flag } of fallbackRoutes) {
      if (featureFlagsStore.isEnabled(flag)) {
        return next({ name: route })
      }
    }

    // This should never happen as main.ts checks for accessible games before mounting
    // But handle it as a safety fallback
    console.error('No accessible games found - this should not happen')
    window.location.href = '/maintenance.html'
    return
  }

  next()
})

export default router
